/* istanbul ignore file */

import { useQuery } from '@tanstack/react-query';
import type { ExtractGetRoutes, RouteSegment } from '@openclassrooms/sdk';
import checkGetRetry from '../services/checkGetRetry';
import get from '../services/get';
import type { ApiRoutes, GetRequestBuilder, RequestOption } from '../types';
import useQueryKeys from './useQueryKeys';
export default function useGet<Path extends ExtractGetRoutes<ApiRoutes> & RouteSegment, RequestBuilder extends GetRequestBuilder<Path> = GetRequestBuilder<Path>, ResponseType = Awaited<ReturnType<RequestBuilder['execute']>>>(path: Path, options?: Omit<RequestOption<RequestBuilder>, 'headers'> & {
  pagination?: {
    rangeStart?: number;
    rangeEnd?: number;
  };
  queryKey?: string;
  watchedQueryKeys?: string[];
  enabled?: boolean;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  headers?: Record<string, string>;
  onSuccess?: (response: ResponseType) => void;
  onError?: (error: Error) => void;
}) {
  const {
    watchedQueryKeys = [],
    queryKey: customQueryKey,
    enabled,
    refetchInterval,
    refetchOnWindowFocus,
    onSuccess,
    onError,
    ...otherOptions
  } = options || {};
  const queryKey = useQueryKeys({
    path,
    customQueryKey,
    options: otherOptions,
    watchedQueryKeys
  });
  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const response = (await get(path, otherOptions as any)) as ResponseType;
        onSuccess?.(response);
        return response;
      } catch (error) {
        onError?.(error as Error);
        throw error;
      }
    },
    staleTime: 5000,
    enabled,
    retry: checkGetRetry,
    ...(refetchInterval === undefined ? {} : {
      refetchInterval
    }),
    ...(refetchOnWindowFocus === undefined ? {} : {
      refetchOnWindowFocus
    })
  });
}